import { ApiFormId } from 'common.types'

export const useUtilities = () => {
  const validateEmail = (email: string) => {
    const normalized = email.trim()
    const defaultRegex =
      /^[A-Za-z0-9._%+-]+@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/
    return defaultRegex.test(normalized)
    // const voterRegistrationRegex = /\w+([-+.]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/
    // const mailInBallotRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
    // if (formType === `voter-registration`) {
    //   return voterRegistrationRegex.test(normalized)
    // } else if (formType === 'mail-in-ballot') {
    //   return mailInBallotRegex.test(normalized)
    // }
  }

  const validatePhoneNumber = (phoneNumber: string): boolean => {
    const phoneRegex = /^(?:\+?\d{1,3})?\d{10}$/
    return (
      phoneRegex.test(phoneNumber) &&
      phoneNumber.replace(/^\+1/, '').length <= 10
    )
  }

  const validateDate = (
    input: string,
    dateOnlyYear: boolean = false,
    maxDate: Date | undefined = undefined,
    minDate: Date | undefined = undefined,
  ): boolean => {
    if (dateOnlyYear) {
      const regex = /^\d{4}$/
      return (
        regex.test(input) &&
        (minDate ? minDate.getFullYear() <= parseInt(input) : true) &&
        (maxDate ? maxDate.getFullYear() >= parseInt(input) : true)
      )
    } else {
      const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/
      return (
        regex.test(input) &&
        (minDate ? minDate.getTime() <= new Date(input).getTime() : true) &&
        (maxDate ? maxDate.getTime() >= new Date(input).getTime() : true)
      )
    }
  }

  const validatePennDotId = (pennDotId: string) => {
    const onlyNumbersRegex = /^[0-9]*$/
    return pennDotId?.length === 8 && onlyNumbersRegex.test(pennDotId)
  }

  const validateSsnLastFour = (ssnLastFour: string) => {
    const onlyNumbersRegex = /^[0-9]*$/
    return ssnLastFour?.length === 4 && onlyNumbersRegex.test(ssnLastFour)
  }

  const validateZip = (zip: string, international: boolean = false) => {
    const transformedZip = zip?.replace(/-/g, '')
    if (international) {
      return transformedZip.length <= 10
    } else {
      const onlyNumbersRegex = /^[0-9]*$/
      return (
        onlyNumbersRegex.test(transformedZip) &&
        [5, 9].includes(transformedZip.length)
      )
    }
  }

  const formatDateToMMDDYYYY = (date: Date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0') // Months are zero-indexed
    const day = String(date.getDate()).padStart(2, '0')
    const year = date.getFullYear()

    return `${month}/${day}/${year}`
  }

  const wait = (ms: number): Promise<void> => {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  const randomString = (length: number) => {
    let result = ''
    const characters =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    const charactersLength = characters.length
    let counter = 0
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength))
      counter += 1
    }
    return result
  }

  const validateSignature = async (base64String: string): Promise<boolean> => {
    if (!base64String) {
      return true
    }
    const img = new Image()
    return new Promise((resolve, reject) => {
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject('Canvas context not available')
          return
        }
        ctx.drawImage(img, 0, 0)
        const imageData = ctx.getImageData(0, 0, img.width, img.height)
        const data = imageData.data

        const totalPixels = img.width * img.height
        let blackPixelCount = 0

        for (let i = 0; i < data.length; i += 4) {
          const red = data[i]
          const green = data[i + 1]
          const blue = data[i + 2]

          if (red === 0 && green === 0 && blue === 0) {
            blackPixelCount++
          }
        }
        const blackPixelPercentage = (blackPixelCount / totalPixels) * 100
        resolve(blackPixelPercentage > 2)
      }

      img.onerror = (err) => reject(err)
      img.src = `${base64String}`
    })
  }

  const scrollToTop = (duration: number) => {
    const start = window.scrollY
    const startTime = performance.now()

    function animateScroll(currentTime: number) {
      const elapsedTime = currentTime - startTime
      const progress = Math.min(elapsedTime / duration, 1)
      const scrollAmount = start * (1 - progress)

      window.scrollTo(0, scrollAmount)

      if (progress < 1) {
        requestAnimationFrame(animateScroll)
      }
    }

    requestAnimationFrame(animateScroll)
  }

  const conditionallyInitializeBlpaGtm = () => {
    setTimeout(() => {
      const urlParams = new URLSearchParams(window.location.search)
      if (urlParams.get('org') !== 'blpa') {
        return
      }

      const script = document.createElement('script')
      script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','GTM-KVDTWDJZ');`

      document.head.insertBefore(script, document.head.firstChild)
    }, 500)
  }

  return {
    wait,
    scrollToTop,
    randomString,
    validateSignature,
    formatDateToMMDDYYYY,
    validateEmail,
    validatePhoneNumber,
    validateDate,
    validatePennDotId,
    validateSsnLastFour,
    validateZip,
    conditionallyInitializeBlpaGtm,
  }
}
